// pages/components/billing/TransactionHistory.jsx
import React, { useState, useEffect } from 'react';
import { Download, FileText, CheckCircle, XCircle, Clock, Search } from 'lucide-react';
import apiRequest from '@/services/requests';

const TransactionHistory = () => {
const [transactions, setTransactions] = useState([]);
const [loading, setLoading] = useState(true);
const [page, setPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const [filter, setFilter] = useState('all');

useEffect(() => {
loadTransactions();
}, [page, filter]);
 
const loadTransactions = async () => {
setLoading(true);
try {
const params = { page, perPage: 10 };
if (filter !== 'all') params.status = filter;

const res = await apiRequest.getTransactionHistory(params);
if (res.success) {
setTransactions(res.data.transactions);
setTotalPages(res.data.totalPages);
}
} catch (error) {
console.error('Error loading transactions:', error);
}
setLoading(false);
};

const getStatusBadge = (status) => {
switch (status) {
case 'completed':
case 'successful':
return (
<div className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
<CheckCircle size={14} />
Completed
</div>
);
case 'failed':
return (
<div className="flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
<XCircle size={14} />
Failed
</div>
);
case 'pending':
return (
<div className="flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
<Clock size={14} />
Pending
</div>
);
default:
return null;
}
};

const formatDate = (date) => {
return new Date(date).toLocaleDateString('en-US', {
year: 'numeric',
month: 'short',
day: 'numeric',
hour: '2-digit',
minute: '2-digit'
});
};

if (loading && page === 1) {
return (
<div className="flex items-center justify-center py-12">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
</div>
);
}

return (
<div className="space-y-6">
{/* Header */}
<div className="flex items-center justify-between">
<div>
<h2 className="text-xl lg:text-2xl font-bold text-slate-900">Transaction History</h2>
<p className="text-slate-600 text-sm mt-1">View all your past transactions and invoices</p>
</div>
</div>

{/* Filters */}
<div className="flex gap-2">
{['all', 'completed', 'pending', 'failed'].map((status) => (
<button
key={status}
onClick={() => {
setFilter(status);
setPage(1);
}}
className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
filter === status
? 'bg-indigo-600 text-white shadow-md'
: 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
}`}
>
{status.charAt(0).toUpperCase() + status.slice(1)}
</button>
))}
</div>

{/* Transactions List */}
{transactions.length === 0 ? (
<div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
<FileText className="mx-auto text-slate-300 mb-4" size={48} />
<h3 className="text-lg font-semibold text-slate-900 mb-2">No Transactions Found</h3>
<p className="text-slate-600">
{filter === 'all' 
? "You haven't made any transactions yet."
: `No ${filter} transactions found.`
}
</p>
</div>
) : (
<div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
<div className="overflow-x-auto">
<table className="w-full">
<thead className="bg-slate-50 border-b border-slate-200">
<tr>
<th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
Date
</th>
<th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
Description
</th>
<th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
Amount
</th>
<th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
Status
</th>
<th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
Invoice
</th>
</tr>
</thead>
<tbody className="divide-y divide-slate-200">
{transactions.map((transaction) => (
<tr key={transaction.id} className="hover:bg-slate-50 transition-colors">
<td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
{formatDate(transaction.createdAt)}
</td>
<td className="px-6 py-4 text-sm text-slate-900">
<div>
<p className="font-medium">{transaction.description || 'Subscription Payment'}</p>
<p className="text-xs text-slate-500 mt-1">
Transaction ID: {transaction.transaction_id || transaction.id}
</p>
</div>
</td>
<td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
${transaction.charged_amount || '0.00'}
</td>
<td className="px-6 py-4 whitespace-nowrap">
{getStatusBadge(transaction.status)}
</td>
<td className="px-6 py-4 whitespace-nowrap text-sm">
{transaction.status === 'completed' || transaction.status === 'success' ? (
<button className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium">
<Download size={16} />
Download
</button>
) : (
<span className="text-slate-400">N/A</span>
)}
</td>
</tr>
))}
</tbody>
</table>
</div>

{/* Pagination */}
{totalPages > 1 && (
<div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
<button
onClick={() => setPage(p => Math.max(1, p - 1))}
disabled={page === 1}
className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
>
Previous
</button>
<span className="text-sm text-slate-600">
Page {page} of {totalPages}
</span>
<button
onClick={() => setPage(p => Math.min(totalPages, p + 1))}
disabled={page === totalPages}
className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
>
Next
</button>
</div>
)}
</div>
)}
</div>
);
};

export default TransactionHistory;